/* eslint-disable */
import { createLogger, serializeError } from 'logging';
import type { Writable } from 'type-fest';

import {
    type Collection,
    ContentClassEnum,
    type ContentHistory,
    Group,
    type V2ApiSearchCollectionsRequest as SearchCollectionsRequest,
    type ContentApi,
    type ContentApiFetchContentRequest,
    type V2Api as CollectionApi,
    type GroupApi,
    LeanContent,
    DownloadFormats,
} from '@videoblocks/content-service-client';

export default class ContentServiceClient {
    private readonly logger = createLogger('ContentServiceClient');

    constructor(
        private readonly collectionApi: CollectionApi,
        private readonly contentApi: ContentApi,
        private readonly groupApi: GroupApi,
    ) {}

    public async fetchStockItemByUrlId(
        urlId: string,
        contentClass: ContentClassEnum,
        params: ContentApiFetchContentRequest = {},
    ): Promise<ContentHistory | undefined> {
        try {
            const searchResult = await this.contentApi.fetchContent({
                urlIds: urlId,
                classes: contentClass,
                limit: 1,
                ...params,
            });
            return searchResult.data?.[0];
        } catch (error) {
            const message = typeof error === 'object' && error instanceof Error ? error?.message : undefined;
            this.logger.warn(
                { error: serializeError(error), urlId, contentClass },
                message ?? 'Error fetching stock item by URL ID',
            );
        }
    }

    public async fetchStockItemByUrlIdLean(
        urlId: string,
        contentClass: ContentClassEnum,
    ): Promise<LeanContent | undefined> {
        try {
            const searchResult = await this.contentApi.fetchLeanContent({
                urlIds: urlId,
                classes: contentClass,
                limit: 1,
                processEncodings: true,
            });
            return searchResult.data?.[0];
        } catch (error) {
            const message = typeof error === 'object' && error instanceof Error ? error?.message : undefined;
            this.logger.warn(
                { error: serializeError(error), urlId, contentClass },
                message ?? 'Error fetching stock item by URL ID Lean',
            );
        }
    }

    public async fetchCollectionByRouteUri(
        routeUri: string,
        contentClass: ContentClassEnum,
    ): Promise<Collection | undefined> {
        try {
            const searchRequest: Writable<SearchCollectionsRequest> = { routeUris: routeUri };

            // Video collections can be cross-asset.
            if (contentClass === ContentClassEnum.Video) {
                searchRequest['includeClasses'] = [ContentClassEnum.Video];
                searchRequest['excludeClasses'] = [ContentClassEnum.Image];
            } else {
                searchRequest.classes = contentClass;
            }
            const searchResult = await this.collectionApi.searchCollections(searchRequest);
            return searchResult.data?.collections?.[0];
        } catch (error) {
            const message = typeof error === 'object' && error instanceof Error ? error?.message : undefined;
            this.logger.warn(
                { error: serializeError(error), routeUri, contentClass },
                message ?? 'Error fetching collection item by route uri',
            );
        }
    }

    public async fetchCategoryByUrlId(urlId: string): Promise<Group | undefined> {
        try {
            const searchResult = await this.groupApi.fetchGroup({
                urlIds: urlId,
                isActive: true,
                limit: 1,
            });
            return searchResult.data?.[0];
        } catch (error) {
            const message = typeof error === 'object' && error instanceof Error ? error?.message : undefined;
            this.logger.warn({ error: serializeError(error), urlId }, message ?? 'Error fetching category by urlId');
        }
    }

    public async fetchDownloadFormatsById(id: number): Promise<Array<DownloadFormats>> {
        try {
            const formats = await this.contentApi.fetchContentDownloadFormats({ contentId: id });
            return formats.data || [];
        } catch (error) {
            const message = typeof error === 'object' && error instanceof Error ? error?.message : undefined;
            this.logger.warn({ error: serializeError(error), id }, message ?? 'Error fetching download formats by ID');
            return [];
        }
    }
}
